import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";
import DOMPurify from "dompurify";
import { SeoHead } from "@/components/SeoHead";
import { absoluteSeoUrl, limitSeoText } from "@/lib/seo";

const CmsPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    api
      .get(`/cms/pages/${slug}`)
      .then((response) => {
        if (!cancelled) {
          setPage(response.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto py-24 px-4 text-center">
        <Loader2 className="animate-spin mx-auto text-[#2D5A47]" size={22} />
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="mx-auto max-w-lg px-4 py-24 text-center" data-testid="cms-page-notfound">
        <div className="text-xs tracking-[0.3em] uppercase text-[#8C6D46] mb-3">404</div>
        <h1 className="font-serif text-5xl font-semibold text-[#1E2A24]">Page not found</h1>
        <p className="mt-4 text-sm text-[#64748B]">This page is unavailable.</p>
        <Link
          to="/"
          className="mt-8 inline-block rounded-lg bg-[#2D5A47] px-6 py-3 font-semibold text-white hover:bg-[#214435]"
        >
          Back to home
        </Link>
      </div>
    );
  }

  const pageBodyText = String(page.body_html || "").replace(/<[^>]+>/g, " ");
  const seo = {
    title: `${page.title} | Eira Products`,
    description: limitSeoText(page.meta_description || page.excerpt || pageBodyText || page.title),
    canonical: absoluteSeoUrl(`/page/${page.slug || slug}`),
    type: "article",
  };

  return (
    <>
      <SeoHead seo={seo} />
      <article
        className="mx-auto max-w-3xl px-4 py-16"
        data-testid={`cms-page-${page.slug}`}
      >
        <div className="mb-3 text-xs uppercase tracking-[0.3em] text-[#8C6D46]">Eira</div>
        <h1 className="mb-8 font-serif text-4xl font-semibold text-[#1E2A24] sm:text-5xl">
          {page.title}
        </h1>
        <div
          className="cms-body prose max-w-none text-sm leading-relaxed text-[#1E2A24] sm:text-base [&>p]:mb-4 [&>h2]:mb-3 [&>h2]:mt-8 [&>h2]:font-serif [&>h2]:text-2xl [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:list-decimal [&>ol]:pl-6 [&_a]:text-[#C86D42] [&_a]:underline"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.body_html || "") }}
        />
        {page.updated_at && (
          <div className="mt-12 text-xs text-[#8C6D46]">
            Last updated: {new Date(page.updated_at).toLocaleDateString("en-IN", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>
        )}
      </article>
    </>
  );
};

export default CmsPage;
